import React from 'react';
import {hydrate, render} from 'react-dom';
import {Provider} from 'react-redux';
import {loadableReady} from '@loadable/component';
import AbacusClientUtils from '@homeaway/abacus-client-utils';
import {configureStore} from '../../shared/startup/configureStore';
import {withErrorBoundary} from '../../shared/components/common/error-boundary/ErrorBoundary';
import LazyLoad from '../../shared/components/common/lazy-load/LazyLoad';
import {LAZY_LOAD_OFFSET} from '../../shared/utils/constants';
import {SearchBoxLite} from '../../shared/components/common/searchboxlite/SearchBoxLite';
import {SearchFormContainer} from '../../shared/components/common/search-form/SearchForm';
import {TESTS, getValue} from '../../shared/utils/abacus';
import {getActionLocation} from '../../shared/utils/edap';
import client from './client';
import {ApolloProvider} from 'react-apollo';
import {LocalizationContextProvider} from '@homeaway/react-localization-components';

const {DEFAULT: LAZY_LOAD_OFFSET_DEFAULT} = LAZY_LOAD_OFFSET;

if (process.env.NODE_ENV === 'development') {
    const whyDidYouRender = require('@welldone-software/why-did-you-render');
    whyDidYouRender(React, {
        trackAllPureComponents: true,
        logOwnerReasons: true,
        collapseGroups: true,
    });
}

const getTemplateComponent = (template) =>
    process.env.NODE_ENV === 'development'
        ? import(
              /* webpackChunkName: "content-[request]" */
              /* webpackPreload: true */
              /* webpackInclude: /\.js$/ */
              /* webpackExclude: /\.spec\.js$/ */
              `../../shared/templates/${template}`
          ).then(({Template}) => Template)
        : import(
              /* webpackChunkName: "content" */
              /* webpackPreload: true */
              '../../shared/App'
          ).then(({App}) => App);

const hydrateContent = (store) => {
    const {
        config: {
            template,
            url: {prefix},
        },
        siteContext,
    } = store.getState();

    const whenTemplate = getTemplateComponent(template);

    const whenLoadableReady = loadableReady();

    Promise.all([whenTemplate, whenLoadableReady]).then(([Template]) => {
        hydrate(
            <ApolloProvider client={client({prefix, siteContext})}>
                <Provider store={store}>
                    <LocalizationContextProvider siteContext={siteContext}>
                        <Template />
                    </LocalizationContextProvider>
                </Provider>
            </ApolloProvider>,
            document.querySelector('.content-root')
        );
    });
};

/* eslint consistent-return:0 */
export default function hydrateApp() {
    // Grab the state from a global injected into server-generated HTML
    const preloadedState = window.__PRELOADED_STATE__;

    // Create Redux store with initial state
    const store = configureStore({
        initialState: preloadedState,
    });

    const {
        abacus,
        config: {
            templateType: {isMultiDestination, isroot},
            template,
        },
        modules: {jumbotron: introText, breadcrumbs},
        siteContext,
    } = store.getState();

    window.__SITE_CONTEXT__ = siteContext;

    const searchFormTest =
        TESTS[`SEARCH_FORM_${getActionLocation(template).toUpperCase()}`];
    const showSearchForm = getValue(abacus, searchFormTest) === 1;
    const aboveFoldTestEnabled =
        getValue(abacus, TESTS.ABOVE_FOLD_REDESIGN) === 1;
    const breadcrumbsTestEnabled =
        getValue(abacus, TESTS.BREADCRUMB_IN_HEADER) === 1;

    const templateSupportsHeaderRedesign =
        template === 'destination-high-level' ||
        template === 'destination-high-level-bot';
    const templateSupportsBreadcrumbs = !(isMultiDestination || isroot);

    const useHeaderRedesign =
        aboveFoldTestEnabled &&
        templateSupportsHeaderRedesign &&
        introText !== null;
    const showBreadcrumbs =
        breadcrumbsTestEnabled && templateSupportsBreadcrumbs;

    AbacusClientUtils.initialize(abacus);

    if (abacus[TESTS.POLYFILL_SERVICE]) {
        AbacusClientUtils.logExperiment(abacus[TESTS.POLYFILL_SERVICE]);
    }

    const SearchComponent = showSearchForm
        ? SearchFormContainer
        : SearchBoxLite;

    hydrate(
        <Provider store={store}>
            {withErrorBoundary(<SearchComponent />)}
        </Provider>,
        document.querySelector('.searchbox-root')
    );

    if (useHeaderRedesign) {
        import(
            /* webpackChunkName: "jumbotron-text" */
            /* webpackPreload: true */
            '../../shared/components/destination/header/JumbotronText'
        ).then((module) => {
            const JumbotronText = module.default;
            hydrate(
                <Provider store={store}>
                    {withErrorBoundary(<JumbotronText />)}
                </Provider>,
                document.querySelector('.jumbotron-text')
            );
        });
    }

    if (breadcrumbs && (useHeaderRedesign || showBreadcrumbs)) {
        import(
            /* webpackChunkName: "breadcrumbs-tracker" */
            '../../shared/components/common/edap-view-tracker/EdapViewTracker'
        ).then((module) => {
            const EdapViewTracker = module.default;
            hydrate(
                <React.Fragment>
                    {withErrorBoundary(
                        <EdapViewTracker
                            eventData={{
                                actionlocation: 'destination-breadcrumb',
                            }}
                        />
                    )}
                </React.Fragment>,
                document.querySelector('.breadcrumbs-tracker')
            );
        });
    }

    import(
        /* webpackChunkName: "site-header" */
        /* webpackPreload: true */
        '../../shared/components/common/site-header/SiteHeader'
    ).then((module) => {
        const SiteHeader = module.default;
        render(
            <Provider store={store}>
                <LocalizationContextProvider siteContext={siteContext}>
                    {withErrorBoundary(<SiteHeader />)}
                </LocalizationContextProvider>
            </Provider>,
            document.querySelector('.site-header-root')
        );
    });

    hydrateContent(store);

    import(
        /* webpackChunkName: "site-footer" */
        '../../shared/components/common/site-footer/AsyncSiteFooter'
    ).then(({AsyncSiteFooter}) => {
        render(
            <Provider store={store}>
                <LazyLoad offset={LAZY_LOAD_OFFSET_DEFAULT}>
                    {withErrorBoundary(<AsyncSiteFooter />)}
                </LazyLoad>
            </Provider>,
            document.querySelector('.site-footer-root')
        );
    });
}
